import React, { useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';

function StyledQRCode({ 
  value, 
  size = 300, 
  fgColor = '#000000',
  bgColor = '#FFFFFF',
  pattern = 'squares',
  cornerStyle = 'square',
  logoUrl = null,
  logoSize = 0.2,
  frameStyle = 'none',
  frameText = '',
  frameColor = '#626ed4'
}) {
  const ref = useRef(null);
  const qrCode = useRef(null);

  useEffect(() => {
    if (!qrCode.current) {
      // Minimum size for qr-code-styling is 100px
      const adjustedSize = Math.max(size, 100);
      
      // Dot type mapping
      const dotsType = pattern === 'dots' ? 'dots' : pattern === 'rounded' ? 'rounded' : 'square';
      
      // Corner dots type
      const cornerDotType = cornerStyle === 'rounded' ? 'dot' : cornerStyle === 'dot' ? 'dot' : 'square';
      
      qrCode.current = new QRCodeStyling({
        width: adjustedSize,
        height: adjustedSize,
        data: value,
        margin: 10,
        qrOptions: {
          typeNumber: 0,
          mode: 'Byte',
          errorCorrectionLevel: 'H'
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: logoSize,
          margin: 5,
          crossOrigin: 'anonymous'
        },
        dotsOptions: {
          color: fgColor,
          type: dotsType
        },
        backgroundOptions: {
          color: bgColor
        },
        image: logoUrl,
        cornersSquareOptions: {
          color: fgColor,
          type: cornerStyle === 'rounded' ? 'extra-rounded' : 'square'
        },
        cornersDotOptions: {
          color: fgColor,
          type: cornerDotType
        }
      });
    }

    if (ref.current) {
      ref.current.innerHTML = '';
      qrCode.current.append(ref.current);
      
      // Add frame if needed
      if (frameStyle !== 'none' && ref.current.firstChild) {
        const canvas = ref.current.firstChild;
        const container = document.createElement('div');
        container.style.position = 'relative';
        container.style.display = 'inline-block';
        
        // Move canvas into container
        ref.current.removeChild(canvas);
        container.appendChild(canvas);
        ref.current.appendChild(container);
        
        // Add frame text
        if (frameText && frameStyle === 'bottom') {
          const textDiv = document.createElement('div');
          textDiv.textContent = frameText;
          textDiv.style.cssText = `
            text-align: center;
            padding: 10px;
            background: ${frameColor};
            color: white;
            font-weight: 600;
            font-size: 14px;
            border-radius: 0 0 8px 8px;
            margin-top: -5px;
          `;
          container.appendChild(textDiv);
        }
        
        // Add circular frame
        if (frameStyle === 'circle') {
          container.style.borderRadius = '50%';
          container.style.overflow = 'hidden';
          container.style.border = `8px solid ${frameColor}`;
        }
        
        // Add rounded frame
        if (frameStyle === 'rounded') {
          container.style.borderRadius = '20px';
          container.style.border = `6px solid ${frameColor}`;
          container.style.padding = '10px';
          container.style.background = bgColor;
        }
      }
    }
  }, [value, size, fgColor, bgColor, pattern, cornerStyle, logoUrl, logoSize, frameStyle, frameText, frameColor]);

  useEffect(() => {
    if (qrCode.current) {
      const adjustedSize = Math.max(size, 100);
      
      qrCode.current.update({
        data: value,
        width: adjustedSize,
        height: adjustedSize,
        image: logoUrl,
        dotsOptions: {
          color: fgColor,
          type: pattern === 'dots' ? 'dots' : pattern === 'rounded' ? 'rounded' : 'square'
        },
        backgroundOptions: {
          color: bgColor
        },
        cornersSquareOptions: {
          color: fgColor,
          type: cornerStyle === 'rounded' ? 'extra-rounded' : 'square'
        }
      });
    }
  }, [value, size, fgColor, bgColor, pattern, cornerStyle, logoUrl]);

  return <div ref={ref} />;
}

export default StyledQRCode;
